import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';

import React, { FC, Suspense, lazy, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { Router, Switch, useLocation } from 'react-router-dom';
import { history } from './appHistory';
import { AuthProvider, PrivateRoute, useAuth0 } from './Auth';
import { env } from './Shared/constants/env';

import * as Sentry from '@sentry/browser';

import './index.css';
import './Shared/styles/fonts.module.css';
import { ToastProvider } from './Shared/components/Toast';
import { Core } from './Core';
import { AgentQuoteRouter } from './QuoteSearch/components/AgentQuoteRouter';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorPage } from '@clearcover/atlas';
import { OutdatedBrowserMessage } from 'Core/components/OutdatedBrowserMessage/OutdatedBrowserMessage';
import { datadogRum } from '@datadog/browser-rum';
import {
  createInstance,
  OptimizelyProvider,
  enums,
} from '@optimizely/react-sdk';
import { randomId } from 'Shared/hooks/useUniqueId/useUniqueId';
import { LoadingPage } from 'Shared/components';
import { QUOTE_APP_ROUTE } from 'Shared/constants/routes';
import { ToggleCallback } from 'Shared/ada/adaApi';

const QuoteApp = lazy(() => import('./QuoteApp'));

const optimizely = createInstance({
  sdkKey: env.optimizelyId,
  logLevel: enums.LOG_LEVEL.ERROR,
});

// ignoring for dev
if (env.datadogEnv !== 'dev') {
  // Configure here:  https://app.datadoghq.com/rum/application/61dea90f-b8eb-4832-8ccf-a62f7b3b0abe/sdk
  datadogRum.init({
    applicationId: '61dea90f-b8eb-4832-8ccf-a62f7b3b0abe',
    clientToken: 'pubc49befc94289a63dce2a64693a69c62b',
    // `site` refers to the Datadog site parameter of your organization
    // see https://docs.datadoghq.com/getting_started/site/
    site: 'datadoghq.com',
    service: 'core-app',
    env: env.datadogEnv,
    // Specify a version number to identify the deployed version of your application in Datadog
    // version: '1.0.0',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 0,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
  });
}

// zendesk
const initializeZendesk = () => {
  if (!!window.zE) {
    try {
      // here we can't detect which version of the widget is loaded, and we will a see console error for the one that fails
      window.zE('webWidget', 'close'); // legacy module
      window.zE('messenger', 'close'); // new module
      const style = document.createElement('style');
      style.id = 'zendesk-styles';
      style.type = 'text/css';
      style.innerHTML = '';
      document.head.appendChild(style);
    } catch (error) {
      console.warn(error);
    }
  }
};
const zendeskScript = document.createElement('script');
zendeskScript.id = 'ze-snippet';
zendeskScript.src = `https://static.zdassets.com/ekr/snippet.js?key=${process.env.REACT_APP_ZENDESK}`;
zendeskScript.onload = initializeZendesk;
zendeskScript.async = true;
document.head.appendChild(zendeskScript);

// ada
const adaScript = document.createElement('script') as HTMLScriptElement & {
  dataHandle: string;
};
adaScript.id = '__ada';
adaScript.setAttribute('data-handle', 'clearcover');

const toggleCallback: ToggleCallback = (isDrawerOpen: boolean) => {
  toggleCallback.handler(isDrawerOpen);
  toggleCallback.isDrawerOpen = isDrawerOpen;
};
toggleCallback.handler = () => {};
window.adaSettings = {
  toggleCallback,
  onAdaEmbedLoaded: () => toggleCallback(false),
};

adaScript.src = 'https://static.ada.support/embed2.js';
adaScript.onload = () => {
  const style = document.createElement('style');
  style.id = 'ada-styles';
  style.type = 'text/css';
  style.innerHTML =
    'iframe#ada-button-frame{display: none} iframe#ada-chat-frame{left: 20px; bottom: 70px !important;}';
  document.head.appendChild(style);
};
document.head.appendChild(adaScript);

Sentry.init({
  environment: env.sentryEnvironment,
  dsn: 'https://fab5244eb309416da8ed56aefed74d56@sentry.io/1804235',
  release: `core-app@${env.versionNumber ? env.versionNumber : 'dev'}`,
});

window.fullstory && window.fullstory.load(env.fullstoryId);

export type FallbackPropsType = {
  error?: Error & { response?: any };
  componentStack?: string;
  resetErrorBoundary: () => void;
};

const ErrorFallback: FC<FallbackPropsType> = ({ error, componentStack }) => {
  return (
    <ErrorPage
      key="error"
      errorMessage={`${error?.message}
          \nContact support with the following reference ID or timestamp:\n${error
            ?.response?.data?.metadata?.responseBody?.supportReferenceId ??
            new Date().toUTCString()}`}
      componentStack={componentStack || ''}
    />
  );
};

const CoreOptimizelyProvider: FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth0();
  const location = useLocation();

  useEffect(() => {
    const isQuoteApp = location.pathname.includes(QUOTE_APP_ROUTE);
    const adaStyleElement = document.getElementById('ada-styles');
    const zendeskStyleElement = document.getElementById('zendesk-styles');
    if (isQuoteApp) {
      if (adaStyleElement) {
        adaStyleElement.innerHTML =
          'iframe#ada-button-frame{display: none} iframe#ada-chat-frame{right: 20px; bottom: 100px !important;}';
      }
      if (zendeskStyleElement) {
        zendeskStyleElement.innerHTML =
          'iframe[title="Messaging window"]{right: 20px!important; left: unset!important; bottom: 150px !important; max-height: calc(100% - 150px) !important;}';
      }
    } else {
      if (adaStyleElement) {
        adaStyleElement.innerHTML =
          'iframe#ada-button-frame{display: none} iframe#ada-chat-frame{left: 20px; bottom: 70px !important;}';
      }
      if (zendeskStyleElement) {
        zendeskStyleElement.innerHTML = '';
      }
    }
  }, [location]);

  return (
    <OptimizelyProvider
      optimizely={optimizely}
      user={{
        id: user?.id ?? randomId(),
        attributes: {
          hostname: window.location.host,
        },
      }}
    >
      {children}
    </OptimizelyProvider>
  );
};

const container = document.getElementById('root');

if (!container) throw new Error('Failed to find the root element');

const root = createRoot(container);

root.render(
  <Router history={history}>
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={err => {
        if (err instanceof Error) {
          Sentry.captureException(err);
        }
      }}
    >
      <AuthProvider>
        <ToastProvider>
          <CoreOptimizelyProvider>
            <Suspense fallback={<LoadingPage />}>
              <Switch>
                <PrivateRoute
                  path="/applications"
                  component={AgentQuoteRouter}
                />
                <PrivateRoute path={QUOTE_APP_ROUTE} component={QuoteApp} />
                <PrivateRoute path="/" component={Core} />
              </Switch>
            </Suspense>
          </CoreOptimizelyProvider>
        </ToastProvider>
      </AuthProvider>
      <OutdatedBrowserMessage />
    </ErrorBoundary>
  </Router>
);
